import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Building2, Users, DollarSign, Calendar, Shield, 
  CheckCircle2, Clock, XCircle, Edit, Loader2, Mail, Copy, Trash2, Plus 
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const SuperAdminPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [editingCompany, setEditingCompany] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteCompany, setInviteCompany] = useState('');
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    checkSuperuserAndFetch();
  }, []);

  const checkSuperuserAndFetch = async () => {
    try {
      const res = await api.get('/admin/is-superuser');
      setIsSuperuser(res.data.is_superuser);
      
      if (res.data.is_superuser) {
        await fetchData();
      }
    } catch (error) {
      console.error('Error checking superuser:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, companiesRes, usersRes, invitesRes, overviewRes] = await Promise.all([
        api.get('/admin/subscriptions'),
        api.get('/admin/companies'),
        api.get('/admin/users'),
        api.get('/admin/invites'),
        api.get('/admin/builders-overview')
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
      setAllUsers(usersRes.data);
      setInvites(invitesRes.data.invites || []);
      setOverview(overviewRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    }
  };

  const handleCreateInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Enter an email address');
      return;
    }
    setCreatingInvite(true);
    try {
      const res = await api.post('/admin/invites', { email: inviteEmail.trim(), company_name: inviteCompany.trim() });
      await navigator.clipboard.writeText(res.data.link).catch(() => {});
      toast.success('Invite created — link copied & emailed');
      setInviteEmail('');
      setInviteCompany('');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleRevokeInvite = async (id) => {
    try {
      await api.delete(`/admin/invites/${id}`);
      toast.success('Invite revoked');
      await fetchData();
    } catch (error) {
      toast.error('Failed to revoke invite');
    }
  };

  const copyLink = async (link) => {
    await navigator.clipboard.writeText(link).catch(() => {});
    toast.success('Link copied to clipboard');
  };

  const handleResendInvite = async (id) => {
    try {
      await api.post(`/admin/invites/${id}/resend`);
      toast.success('Invite resent — expiry extended 14 days');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invite');
    }
  };

  const daysLeft = (expiresAt) => {
    if (!expiresAt) return null;
    const ms = new Date(expiresAt).getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  };

  const handleUpdateSubscription = async () => {
    if (!editingCompany || !newStatus) return;
    
    setSaving(true);
    try {
      await api.put(`/admin/companies/${editingCompany.id}/subscription`, {
        subscription_status: newStatus
      });
      toast.success('Subscription updated');
      setEditingCompany(null);
      await fetchData();
    } catch (error) {
      toast.error('Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500/20 text-green-400"><CheckCircle2 className="h-3 w-3 mr-1" />Active</Badge>;
      case 'trial':
        return <Badge className="bg-yellow-500/20 text-yellow-400"><Clock className="h-3 w-3 mr-1" />Trial</Badge>;
      case 'expired':
        return <Badge className="bg-red-500/20 text-red-400"><XCircle className="h-3 w-3 mr-1" />Expired</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isSuperuser) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Shield className="h-16 w-16 text-red-400 mb-4" />
        <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
        <p className="text-muted-foreground">You don't have permission to access the admin panel.</p>
        <p className="text-sm text-muted-foreground mt-2">Only the platform superuser can access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="super-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Platform Admin</h1>
          <p className="text-muted-foreground">Manage all companies and subscriptions</p>
        </div>
        <Badge className="bg-primary/20 text-primary">
          <Shield className="h-4 w-4 mr-1" /> Superuser
        </Badge>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-primary/20">
                  <Building2 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Companies</p>
                  <p className="text-2xl font-bold">{stats.total_companies}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-green-500/20">
                  <CheckCircle2 className="h-6 w-6 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Subscriptions</p>
                  <p className="text-2xl font-bold">{stats.active_subscriptions}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-blue-500/20">
                  <Users className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-2xl font-bold">{stats.total_users}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-emerald-500/20">
                  <DollarSign className="h-6 w-6 text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Monthly Revenue</p>
                  <p className="text-2xl font-bold">${stats.monthly_revenue.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for Companies and Users */}
      <Tabs defaultValue="companies" className="w-full">
        <TabsList>
          <TabsTrigger value="companies">Companies ({companies.length})</TabsTrigger>
          <TabsTrigger value="users">All Users ({allUsers.length})</TabsTrigger>
          <TabsTrigger value="invites" data-testid="invites-tab">Invites ({invites.length})</TabsTrigger>
        </TabsList>
        
        <TabsContent value="companies">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle>All Companies</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Users</TableHead>
                    <TableHead>Monthly Cost</TableHead>
                    <TableHead>Trial Ends</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companies.map((company) => (
                    <TableRow key={company.id}>
                      <TableCell className="font-medium">{company.name}</TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{company.owner?.name || 'N/A'}</p>
                          <p className="text-xs text-muted-foreground">{company.owner?.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(company.subscription_status)}</TableCell>
                      <TableCell>{company.team_count}</TableCell>
                      <TableCell>${company.monthly_cost}/mo</TableCell>
                      <TableCell>
                        {company.trial_end ? new Date(company.trial_end).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setEditingCompany(company);
                            setNewStatus(company.subscription_status);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="users">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle>All Users</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Companies</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{user.role}</Badge>
                      </TableCell>
                      <TableCell>
                        {user.companies && user.companies.length > 0 ? (
                          <div className="flex flex-col gap-1">
                            {user.companies.map((c, i) => (
                              <span key={i} className="text-xs">
                                {c.name} <span className="text-muted-foreground">({c.role})</span>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invites">
          {overview && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="builders-overview">
              <Card className="bg-card border-border"><CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Active builders</p>
                <p className="text-2xl font-bold">{overview.active_builders}</p>
              </CardContent></Card>
              <Card className="bg-card border-border"><CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Pending invites</p>
                <p className="text-2xl font-bold text-yellow-400">{overview.pending_invites}</p>
              </CardContent></Card>
              <Card className="bg-card border-border"><CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Expiring soon (≤3d)</p>
                <p className="text-2xl font-bold text-red-400">{overview.expiring_soon}</p>
              </CardContent></Card>
              <Card className="bg-card border-border"><CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Activated</p>
                <p className="text-2xl font-bold text-green-400">{overview.used_invites}</p>
              </CardContent></Card>
            </div>
          )}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle>Invite a New Builder</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div className="space-y-2">
                  <Label htmlFor="invite-email">Email *</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    placeholder="builder@company.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    data-testid="invite-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-company">Company name (optional)</Label>
                  <Input
                    id="invite-company"
                    placeholder="ABC Builders Pty Ltd"
                    value={inviteCompany}
                    onChange={(e) => setInviteCompany(e.target.value)}
                    data-testid="invite-company-input"
                  />
                </div>
                <Button onClick={handleCreateInvite} disabled={creatingInvite} data-testid="create-invite-btn">
                  {creatingInvite ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Create & Send Invite
                </Button>
              </div>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Mail className="h-3 w-3" /> The invite link is emailed to the recipient and copied to your clipboard.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border mt-4">
            <CardHeader>
              <CardTitle>Sent Invites</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invites.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No invites yet</TableCell></TableRow>
                  ) : invites.map((inv) => (
                    <TableRow key={inv.id} data-testid={`invite-row-${inv.email}`}>
                      <TableCell className="font-medium">{inv.email}</TableCell>
                      <TableCell>{inv.company_name || '-'}</TableCell>
                      <TableCell>
                        <Badge className={inv.status === 'used' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                          {inv.status === 'used' ? 'Used' : 'Pending'}
                        </Badge>
                      </TableCell>
                      <TableCell>{inv.status === 'used' ? '—' : (() => {
                        const d = daysLeft(inv.expires_at);
                        if (d === null) return '-';
                        if (d < 0) return <span className="text-red-400">Expired</span>;
                        if (d <= 3) return <span className="text-yellow-400">{d} day{d !== 1 ? 's' : ''} left</span>;
                        return <span className="text-muted-foreground">{d} days left</span>;
                      })()}</TableCell>
                      <TableCell className="text-right space-x-1">
                        {inv.status !== 'used' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => copyLink(inv.link)} title="Copy link" data-testid={`copy-invite-${inv.email}`}>
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleResendInvite(inv.id)} title="Resend & extend expiry" data-testid={`resend-invite-${inv.email}`}>
                              <Mail className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleRevokeInvite(inv.id)} title="Revoke" data-testid={`revoke-invite-${inv.email}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Edit Subscription Dialog */}
      <Dialog open={!!editingCompany} onOpenChange={() => setEditingCompany(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Subscription</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm font-medium mb-1">Company</p>
              <p className="text-lg">{editingCompany?.name}</p>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Subscription Status</p>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCompany(null)}>Cancel</Button>
            <Button onClick={handleUpdateSubscription} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminPage;
